import React, { useState, useEffect } from 'react';
import { medicationsAPI, prescriptionsAPI } from '../services/api';
import { Pill, Plus, Clock, CheckCircle, Upload, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export const Medications = () => {
  const [medications, setMedications] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [showMedForm, setShowMedForm] = useState(false);
  const [showRxForm, setShowRxForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [medForm, setMedForm] = useState({
    name: '',
    dosage: '',
    frequency: 'daily',
    time_schedule: [],
    notes: ''
  });
  const [rxForm, setRxForm] = useState({
    medication_name: '',
    file_url: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [medsRes, rxRes] = await Promise.all([
        medicationsAPI.getAll(),
        prescriptionsAPI.getAll()
      ]);
      setMedications(medsRes.data);
      setPrescriptions(rxRes.data);
    } catch (error) {
      toast.error('Failed to load medications');
    } finally {
      setLoading(false);
    }
  };

  const handleMedSubmit = async (e) => {
    e.preventDefault();
    try {
      await medicationsAPI.create({
        ...medForm,
        time_schedule: medForm.time_schedule.split(',').map(t => t.trim())
      });
      toast.success('Medication added!');
      setShowMedForm(false);
      setMedForm({ name: '', dosage: '', frequency: 'daily', time_schedule: [], notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add medication');
    }
  };

  const handleRxSubmit = async (e) => {
    e.preventDefault();
    try {
      await prescriptionsAPI.create(rxForm);
      toast.success('Prescription uploaded! Awaiting verification.');
      setShowRxForm(false);
      setRxForm({ medication_name: '', file_url: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to upload prescription');
    }
  };

  const logAdherence = async (medId, taken) => {
    try {
      const response = await medicationsAPI.logAdherence(medId, {
        taken,
        scheduled_time: new Date().toISOString(),
        actual_time: taken ? new Date().toISOString() : null
      });
      toast.success(taken ? '✓ Marked as taken' : '✗ Marked as missed');
      console.log('Adherence logged:', response);
    } catch (error) {
      console.error('Adherence error:', error);
      toast.error('Failed to log adherence');
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F2EB] py-8" data-testid="medications-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#2D3A3A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Medications
            </h1>
            <p className="text-[#6B7C7C]">Manage your medications and prescriptions</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowRxForm(true)}
              className="flex items-center space-x-2 px-4 py-2 rounded-full bg-[#79A8E0] text-white hover:bg-[#6897CF]"
              data-testid="upload-prescription-button"
            >
              <Upload className="w-4 h-4" />
              <span>Upload Rx</span>
            </button>
            <button
              onClick={() => setShowMedForm(true)}
              className="flex items-center space-x-2 px-6 py-3 rounded-full bg-[#8FB339] text-white btn-clay"
              data-testid="add-medication-button"
            >
              <Plus className="w-5 h-5" />
              <span>Add Medication</span>
            </button>
          </div>
        </div>

        {showMedForm && (
          <div className="bg-white rounded-2xl p-6 mb-8 card-soft">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>Add Medication</h2>
            <form onSubmit={handleMedSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Medication Name</label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 rounded-xl border border-[#D1CDC5] focus:ring-2 focus:ring-[#8FB339]"
                    value={medForm.name}
                    onChange={(e) => setMedForm({ ...medForm, name: e.target.value })}
                    data-testid="med-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Dosage</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., 10mg"
                    className="w-full px-4 py-2 rounded-xl border border-[#D1CDC5] focus:ring-2 focus:ring-[#8FB339]"
                    value={medForm.dosage}
                    onChange={(e) => setMedForm({ ...medForm, dosage: e.target.value })}
                    data-testid="med-dosage-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Frequency</label>
                  <select
                    className="w-full px-4 py-2 rounded-xl border border-[#D1CDC5] focus:ring-2 focus:ring-[#8FB339]"
                    value={medForm.frequency}
                    onChange={(e) => setMedForm({ ...medForm, frequency: e.target.value })}
                    data-testid="med-frequency-select"
                  >
                    <option value="daily">Daily</option>
                    <option value="twice-daily">Twice Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="as-needed">As Needed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Time Schedule</label>
                  <input
                    type="text"
                    placeholder="e.g., 9:00 AM, 9:00 PM"
                    className="w-full px-4 py-2 rounded-xl border border-[#D1CDC5] focus:ring-2 focus:ring-[#8FB339]"
                    value={medForm.time_schedule}
                    onChange={(e) => setMedForm({ ...medForm, time_schedule: e.target.value })}
                    data-testid="med-schedule-input"
                  />
                </div>
              </div>
              <div className="flex space-x-3">
                <button type="submit" className="px-6 py-2 rounded-full bg-[#8FB339] text-white font-semibold" data-testid="med-submit">
                  Add Medication
                </button>
                <button type="button" onClick={() => setShowMedForm(false)} className="px-6 py-2 rounded-full bg-[#E6E2D6] text-[#2D3A3A]" data-testid="med-cancel">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {showRxForm && (
          <div className="bg-white rounded-2xl p-6 mb-8 card-soft">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>Upload Prescription</h2>
            <form onSubmit={handleRxSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Medication Name</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 rounded-xl border border-[#D1CDC5] focus:ring-2 focus:ring-[#79A8E0]"
                  value={rxForm.medication_name}
                  onChange={(e) => setRxForm({ ...rxForm, medication_name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Prescription File URL</label>
                <input
                  type="url"
                  required
                  placeholder="https://..."
                  className="w-full px-4 py-2 rounded-xl border border-[#D1CDC5] focus:ring-2 focus:ring-[#79A8E0]"
                  value={rxForm.file_url}
                  onChange={(e) => setRxForm({ ...rxForm, file_url: e.target.value })}
                />
                <p className="text-xs text-[#6B7C7C] mt-1">Upload your prescription image to a service and paste the URL</p>
              </div>
              <div className="flex space-x-3">
                <button type="submit" className="px-6 py-2 rounded-full bg-[#79A8E0] text-white font-semibold">
                  Upload
                </button>
                <button type="button" onClick={() => setShowRxForm(false)} className="px-6 py-2 rounded-full bg-[#E6E2D6] text-[#2D3A3A]">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div>
            <h2 className="text-2xl font-bold text-[#2D3A3A] mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>Active Medications</h2>
            <div className="space-y-4">
              {medications.map((med) => (
                <div key={med.id} className="bg-white rounded-2xl p-6 card-soft" data-testid="medication-item">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-[#8FB339]/10 flex items-center justify-center">
                        <Pill className="w-6 h-6 text-[#8FB339]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-[#2D3A3A]">{med.name}</h3>
                        <p className="text-sm text-[#6B7C7C]">{med.dosage} - {med.frequency}</p>
                      </div>
                    </div>
                  </div>
                  {med.time_schedule && med.time_schedule.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {med.time_schedule.map((time, idx) => (
                        <span key={idx} className="flex items-center space-x-1 px-3 py-1 bg-[#F5F2EB] rounded-full text-sm">
                          <Clock className="w-3 h-3" />
                          <span>{time}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => logAdherence(med.id, true)}
                      className="flex-1 py-2 rounded-full bg-[#8FB339]/10 text-[#8FB339] hover:bg-[#8FB339]/20 font-semibold"
                      data-testid="log-taken"
                    >
                      ✓ Taken
                    </button>
                    <button
                      onClick={() => logAdherence(med.id, false)}
                      className="flex-1 py-2 rounded-full bg-[#D66D6D]/10 text-[#D66D6D] hover:bg-[#D66D6D]/20 font-semibold"
                      data-testid="log-missed"
                    >
                      × Missed
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-[#2D3A3A] mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>Prescriptions</h2>
            <div className="space-y-4">
              {prescriptions.map((rx) => (
                <div key={rx.id} className="bg-white rounded-2xl p-6 card-soft">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-[#2D3A3A] mb-1">{rx.medication_name}</h3>
                      <div className="flex items-center space-x-2">
                        {rx.status === 'verified' && (
                          <span className="px-3 py-1 bg-[#8FB339]/10 text-[#8FB339] rounded-full text-sm flex items-center space-x-1">
                            <CheckCircle className="w-3 h-3" />
                            <span>Verified</span>
                          </span>
                        )}
                        {rx.status === 'pending' && (
                          <span className="px-3 py-1 bg-[#E0C079]/10 text-[#E0C079] rounded-full text-sm flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>Pending</span>
                          </span>
                        )}
                        {rx.status === 'rejected' && (
                          <span className="px-3 py-1 bg-[#D66D6D]/10 text-[#D66D6D] rounded-full text-sm flex items-center space-x-1">
                            <AlertCircle className="w-3 h-3" />
                            <span>Rejected</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-[#6B7C7C] mt-2">
                    Uploaded {new Date(rx.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};